<template>
  <div v-if="isReadyToDraw">
    <MFITTHeader
      header-title="座席選択"
      :click-back-button-event="backToBooking2Page"
    />

    <!-- 入力した日付、区間、人数を表示 -->
    <!-- サルーン席の予約変更の場合、inputtedInfoOnBooking.isSaloon:true -->
    <MFITTTicketInfo
      :selected-train-info="selectedTrainInfo"
      :is-saloon="inputtedInfoOnBooking.isSaloon"
    />

    <div class="form">
      <!-- 座席種類 -->
      <MFITTBookingSelectSeatType
        v-model:selectedSeatType="selectedSeatType"
        :selected-train-info="selectedTrainInfo"
        :is-show-change-saloon-caution="isChangedBetweenSaloonAndOthers"
      />

      <!-- 座席指定方法 -->
      <MFITTBookingSeatSelectionMethod
        v-model:seatSelectionRadio="seatSelectionRadio"
        v-model:selectedSeatType="selectedSeatType"
      />

      <!-- 条件で指定を選択 -->
      <div v-if="seatSelectionRadio == 0">
        <!-- 号車指定 -->
        <MFITTBookingSelectCarNumber
          v-model:selectedCar="selectedCar"
          :cars="selectedTrainInfo.cars_formation.cars"
        />

        <!-- 席が離れた場合は -->
        <!-- count>1の時に、分割予約の可否 -->
        <MFITTBookingSelectSeatDivision
          v-if="isMultiDivision"
          v-model:seatDivisionRadio="seatDivisionRadio"
        />
        <!-- 席が離れた場合は -->

        <!-- 窓際の指定 -->
        <!-- 通路側:0 窓側:6 窓側以外中止:4 座席指定:7 サルーン:8 -->
        <MFITTBookingWindowSpecification
          v-else
          v-model:windowSpecificationRadio="windowSpecificationRadio"
        />
        <!-- 窓際の指定 -->
      </div>

      <!-- 購入/予約選択 変更/引換券使用の場合は非表示 -->
      <MFITTBookingSelectBookingOrPurchase
        v-if="
          !(
            inputtedInfoOnBooking.isReservedChange ||
            inputtedInfoOnBooking.isPurchasedChange ||
            isUseCoupon
          )
        "
        v-model:bookingOrPurchaseRadio="bookingOrPurchaseRadio"
        :is-reservation-only-disabled="isReservationOnlyDisabled"
      />

      <ItemButton
        action-type="btn-primary"
        label="次へ"
        :disabled="
          isChangedBetweenSaloonAndOthers ||
          selectedSeatType == '選択してください'
        "
        @clicked="goToNextPage"
      />
    </div>
  </div>

  <!-- クレカ登録なしの場合のエラーダイアログ -->
  <DialogNotRegisteredCreditCard
    ref="DialogNotRegisteredCreditCard"
    @goToNextPage="goToNextPageOnErrorDialog"
  />

  <DialogNotExistSaloon
    ref="DialogNotExistSaloon"
    :close-button-clicked="backToBooking2Page"
  />

  <DialogObservationSeatWarning ref="DialogObservationSeatWarning" />

  <DialogInitialLoading v-if="isShowLoading" :timeout-seconds="21000" />
</template>

<script>
import { onMounted, computed, watch, watchEffect, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

import mfittService from "@/services/MFITT";
import userInfoService from "@/services/userInfo";
import commonMethodsMixin from "@/mixins/Common/CommonMethodsMixin";
import { COMMON_SEAT_TYPES_NAME } from "@/lib/constants";
import API from "@/API";

export default {
  name: "TemplateMfittBooking3",
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const selectedCoupon = store.getters["MFITT/selectedCoupon"];
    const isUseCoupon = selectedCoupon != null;
    const isSubscription = selectedCoupon?.type == "subscription";
    const isExchange = selectedCoupon?.type == "exchange";

    const isReadyToDraw = ref(false);
    const isShowLoading = ref(false);
    const DialogNotRegisteredCreditCard = ref(null);
    const DialogNotExistSaloon = ref(null);
    const DialogObservationSeatWarning = ref(null);

    // 座席指定方法
    const seatSelectionRadio = ref(0);
    const isChangedBetweenSaloonAndOthers = ref(false);

    // 号車の指定
    const selectedCar = ref("指定しない");

    // 窓際の指定(人数1の場合)
    const windowSpecificationRadio = ref({ id: 0, label: "通路側を優先" });

    // 席が離れた場合は(人数2以上の場合)
    const seatDivisionRadio = ref({ id: 3, label: "予約する" });

    // 購入/予約選択 購入:false/予約:true
    const bookingOrPurchaseRadio = ref(false);

    const vacancyInquiry = computed(
      () => store.getters["MFITT/vacancyInquiry"]
    );

    // 選ばれた列車情報
    const selectedTrainInfo = computed(
      () => store.getters["MFITT/vacancyInquiry"].train_info[route.params.id]
    );

    // フロントで管理してる入力された値
    const inputtedInfoOnBooking = computed(
      () => store.getters["MFITT/inputtedInfoOnBooking"]
    );

    // 人数が1以上かのフラグ
    const isMultiDivision = computed(
      () =>
        inputtedInfoOnBooking.value.adult_count +
          inputtedInfoOnBooking.value.child_count >
        1
    );

    const isShowObservationSeatWarning = computed(
      () =>
        ["前展望席", "後展望席"].includes(selectedSeatType.value.name) &&
        (bookingOrPurchaseRadio.value ||
          inputtedInfoOnBooking.value.isReservedChange)
    );

    // 座席種別
    const selectedSeatType =
      ref(
        selectedTrainInfo.value.seat_types.find(
          (seat) => seat.name === "一般席"
        )
      ) || ref("選択してください");

    // 予約のみを選択可能かどうか？(true:選択不可)
    const isReservationOnlyDisabled = computed(() =>
      mfittService.isOverReservationLimit(
        vacancyInquiry.value.boarding_date,
        selectedTrainInfo.value.departure_at
      )
    );

    // 次へボタンで画面での入力条件によって処理を分ける
    const goToNextPage = async () => {
      isShowLoading.value = true;

      try {
        if (isShowObservationSeatWarning.value) {
          isShowLoading.value = false;
          return DialogObservationSeatWarning.value.openDialog();
        }

        // シートマップで指定を選んだ場合
        if (seatSelectionRadio.value == 1) {
          setInputtedInfoOnSeatSelectionRadio1();
          goToSeatMapPage();
        } else {
          // 条件で指定
          setInputtedInfoOnSeatSelectionRadio0();
          // goToBookingConfirmation();
          if (
            inputtedInfoOnBooking.value?.is_reservated ||
            inputtedInfoOnBooking.value?.isPurchasedChange ||
            isSubscription ||
            isExchange
          ) {
            goToBookingConfirmation();
          } else {
            goToSelectPaymentMethod();
          }
        }
      } finally {
        isShowLoading.value = false;
      }
    };

    const openDialogNotRegisteredCreditCard = () => {
      DialogNotRegisteredCreditCard.value.openDialog();
    };

    const goToNextPageOnErrorDialog = async () => {
      // 予約のみで続けるため、予約/購入フラグをtrueに変更
      bookingOrPurchaseRadio.value = true;

      if (isShowObservationSeatWarning.value) {
        DialogNotRegisteredCreditCard.value.closeDialog();
        DialogObservationSeatWarning.value.openDialog();

        return;
      }

      await goToNextPage();
    };

    // 条件指定で選ばれたそれぞれの条件と、号車情報を格納
    const setInputtedInfoOnSeatSelectionRadio0 = () => {
      const addInfo = {
        method: "条件指定",
        special_designation: windowSpecificationRadio.value,
        car_id: selectedCar.value == "指定しない" ? 0 : selectedCar.value.id,
        car_number:
          selectedCar.value == "指定しない" ? 0 : selectedCar.value.number,
        seat_type: selectedSeatType.value,
        is_reservated: bookingOrPurchaseRadio.value,
      };

      // 複数人なら席が離れた場合の値も追加
      if (isMultiDivision.value) {
        addInfo["division"] = seatDivisionRadio.value;
      }
      console.log("addInfo :>> ", addInfo);
      store.commit("MFITT/setInputtedInfoOnBooking", {
        ...inputtedInfoOnBooking.value,
        ...addInfo,
      });
    };

    onMounted(() => {
      // この画面での入力情報がstoreにある場合はセットする
      // methodの有無で判別
      if (inputtedInfoOnBooking.value.method) {
        // 座席種別
        selectedSeatType.value = inputtedInfoOnBooking.value.seat_type;

        // 条件指定の場合
        if (inputtedInfoOnBooking.value.method == "条件指定") {
          // 座席指定方法
          seatSelectionRadio.value = 0;
          // 号車の指定
          if (inputtedInfoOnBooking.value.car_id == 0) {
            selectedCar.value = "指定しない";
          } else {
            selectedCar.value =
              selectedTrainInfo.value.cars_formation.cars.find(
                (car) => car.id == inputtedInfoOnBooking.value.car_id
              );
          }
          // 窓際の指定
          windowSpecificationRadio.value =
            inputtedInfoOnBooking.value.special_designation;
          // シートマップ指定の場合
        } else {
          // 座席指定方法
          seatSelectionRadio.value = 1;
        }

        // 購入/予約選択
        bookingOrPurchaseRadio.value =
          inputtedInfoOnBooking.value.is_reservated;
      }
      // 予約変更から遷移してきた場合
      else if (
        inputtedInfoOnBooking.value.isReservedChange ||
        inputtedInfoOnBooking.value.isPurchasedChange
      ) {
        const myLimitedExpressTicket =
          store.getters["MFITT/myLimitedExpressTicket"];

        const seatSpecialCarTypeName =
          commonMethodsMixin.methods.displaySeatExclusiveCar(
            myLimitedExpressTicket.train.cars_formation.cars,
            myLimitedExpressTicket.seats
          );

        const seatType = selectedTrainInfo.value.seat_types.find(
          (seat) => seat.name === seatSpecialCarTypeName
        );

        if (seatType) {
          selectedSeatType.value = {
            name: seatSpecialCarTypeName,
            car_number: myLimitedExpressTicket.seats[0].car_number,
            cars_special_designation: seatType.cars_special_designation,
          };
        } else {
          const seatAttribute =
            commonMethodsMixin.methods.displayMfittSeatAttribute(
              myLimitedExpressTicket.seats[0].seat_attribute
            );
          const viewSeatType = selectedTrainInfo.value.seat_types.find(
            (seat) =>
              [
                COMMON_SEAT_TYPES_NAME.FRONT_VIEW,
                COMMON_SEAT_TYPES_NAME.REAR_VIEW,
              ].includes(seatAttribute) && seat.name === seatAttribute
          );
          selectedSeatType.value =
            viewSeatType ||
            selectedTrainInfo.value.seat_types.find(
              (seat) => seat.name === COMMON_SEAT_TYPES_NAME.GENERAL
            );
        }
        // サルーンの予約/購入の変更時のみ、サルーンが存在しない選択した列車にない場合は、ダイアログ表示して前画面に戻す
        if (
          inputtedInfoOnBooking.value.isSaloon &&
          !mfittService.isExistMySeatAttribute(
            myLimitedExpressTicket.seats[0].seat_attribute,
            selectedTrainInfo.value
          )
        ) {
          DialogNotExistSaloon.value.openDialog();
          return;
        }
        // 一般席でない場合は、条件指定のラジオをシートマップに設定
        if (selectedSeatType.value.name != "一般席") {
          seatSelectionRadio.value = 1;
        }
        // 予約変更(isReservedChange:true)の場合は、購入/予約のラジオの内部値をtrueに
        bookingOrPurchaseRadio.value =
          inputtedInfoOnBooking.value.isReservedChange;
      }
      isReadyToDraw.value = true;
    });

    watchEffect(() => {
      // サルーンとそれ以外で変更しようとした場合、エラーを出すためのフラグ
      if (
        inputtedInfoOnBooking.value.isReservedChange ||
        inputtedInfoOnBooking.value.isPurchasedChange
      ) {
        isChangedBetweenSaloonAndOthers.value =
          (inputtedInfoOnBooking.value.isSaloon &&
            selectedSeatType.value.name != "サルーン") ||
          (!inputtedInfoOnBooking.value.isSaloon &&
            selectedSeatType.value.name == "サルーン");
      }
    });

    // 最低限の情報を格納
    const setInputtedInfoOnSeatSelectionRadio1 = () => {
      // 他のspecial_designationのvalueと若干異なるので注意(仕様書チェック)
      let specialDesignation = {
        id: selectedSeatType.value.cars_special_designation,
        label: selectedSeatType.value.name,
      };
      const addInfo = {
        method: "シート指定",
        special_designation: specialDesignation,
        seat_type: selectedSeatType.value,
        is_reservated: bookingOrPurchaseRadio.value,
      };
      console.log("addInfo :>> ", addInfo);
      store.commit("MFITT/setInputtedInfoOnBooking", {
        ...inputtedInfoOnBooking.value,
        ...addInfo,
      });
    };

    // Booking4へ
    const goToSeatMapPage = () => {
      router.push({
        name: "MfittBooking4",
        query: {
          coupon_id: route.query.coupon_id,
          changing_limited_express_ticket_id:
            route.query.changing_limited_express_ticket_id,
        },
      });
    };

    // 確認画面へ
    const goToBookingConfirmation = () => {
      router.push({
        name: "MfittBookingConfirmation",
        params: { id: route.params.id },
        query: {
          coupon_id: route.query.coupon_id,
          changing_limited_express_ticket_id:
            route.query.changing_limited_express_ticket_id,
        },
      });
    };

    const goToSelectPaymentMethod = () => {
      router.push({
        name: "MfittSelectPaymentMethod",
        params: { id: route.params.id },
        query: {
          coupon_id: route.query.coupon_id,
          changing_limited_express_ticket_id:
            route.query.changing_limited_express_ticket_id,
        },
      });
    };

    // headerのbackButton用の処理。
    // 条件に合う座席がないエラーで画面遷移してきた場合等にうまく動作しないので、明示的にbooking2を指定して戻る
    const backToBooking2Page = () => {
      router.push({
        name: "MfittBooking2",
        query: {
          coupon_id: route.query.coupon_id,
          changing_limited_express_ticket_id:
            route.query.changing_limited_express_ticket_id,
        },
      });
    };

    return {
      isReadyToDraw,
      isUseCoupon,
      seatSelectionRadio,
      selectedSeatType,
      selectedCar,
      windowSpecificationRadio,
      seatDivisionRadio,
      bookingOrPurchaseRadio,
      selectedTrainInfo,
      inputtedInfoOnBooking,
      isMultiDivision,
      isChangedBetweenSaloonAndOthers,
      isReservationOnlyDisabled,
      isShowLoading,
      goToNextPage,
      goToNextPageOnErrorDialog,
      backToBooking2Page,
      DialogNotRegisteredCreditCard,
      DialogNotExistSaloon,
      DialogObservationSeatWarning,
    };
  },
};
</script>

<style lang="scss" scoped>
.form {
  margin: 20px 15px;
}
</style>
